// frontend/src/pages/Dashboard.js - Updated with API integration to match mobile
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Trophy, Search, Home, Grid, User, TrendingUp, Star, X, Trash2, LogOut, AlertCircle, History, Zap, Target, Dribbble, Activity, MapPin, Sparkles, ChevronLeft, Shield, Flame, Wallet as WalletIcon } from 'lucide-react';
import { placeBet as placeBetApi } from '../utils/settlementUtils';
import { formatMoney } from '../utils/money';
import LiveScoresStrip from '../components/LiveScoresStrip';

// Aligned with mobile APP_CONFIG (MIN_BET_AMOUNT / MAX_BET_AMOUNT)
const MIN_BET_AMOUNT = 100;
const MAX_BET_AMOUNT = 500;

// Bet slip drawer width: covers most of the viewport on phones, caps on desktop.
const DRAWER_MAX_WIDTH = 460;

// Odds band - picks must be near a coin-flip for flat-multiplier fairness.
// MUST match the backend BetLimits (MinOdds / MaxOdds) and the mobile app.
const MIN_PICK_ODDS = 1.83;
const MAX_PICK_ODDS = 2.60;
const inBand = (odds) => !!odds && odds >= MIN_PICK_ODDS && odds <= MAX_PICK_ODDS;

// World Cup anytime-goalscorer props are yes/no bets modeled as a 0.5-goal line -
// show "TO SCORE"/"NO GOAL" instead of OVER/UNDER 0.5
const isGoalscorerStat = (stat) => /goalscorer/i.test(stat || '');

// Long feed names (e.g. "Ederson Jose dos Santos Lourenco da Silva") overflow the
// prop card. Shorten to first + last name for DISPLAY only - the full name stays
// on the pick for settlement matching.
const shortenPlayerName = (name) => {
  const n = String(name || '').trim();
  if (n.length <= 18) return n;
  const parts = n.split(/\s+/);
  if (parts.length <= 2) return n;
  return `${parts[0]} ${parts[parts.length - 1]}`;
};

const Dashboard = (props) => {
  const { user, onLogout, onNavigateToUserBets, onNavigateToWallet, onNavigateToProfile, onNavigateToCopilot } = props;
  const [activeSport, setActiveSport] = useState(null);
  const [availableSports, setAvailableSports] = useState([]);
  const [sportsLoading, setSportsLoading] = useState(true);
  const [bettingSlip, setBettingSlip] = useState([]);
  const [showBettingSlip, setShowBettingSlip] = useState(false);

  // --- Bet slip drawer (slides in from the right; drag the left edge to close) ---
  // Kept mounted through the close animation so the slide-out plays before unmount.
  const [slipMounted, setSlipMounted] = useState(false);
  const [slipOpen, setSlipOpen] = useState(false);   // drives the CSS slide transition
  const panelRef = useRef(null);
  const backdropRef = useRef(null);
  const dragStart = useRef(null);                     // { x, width } captured on pointer down
  const dragCurrent = useRef(0);                      // latest drag offset (px)

  const SLIDE = 'transform 0.28s cubic-bezier(0.22, 1, 0.36, 1)';

  // Mount then slide in on open; slide out then unmount on close.
  useEffect(() => {
    if (showBettingSlip) {
      setSlipMounted(true);
      // Next frame so the browser paints the off-screen state before transitioning.
      const id = requestAnimationFrame(() => setSlipOpen(true));
      return () => cancelAnimationFrame(id);
    }
    setSlipOpen(false); // triggers the slide-out; onTransitionEnd unmounts
  }, [showBettingSlip]);

  const closeSlip = useCallback(() => setShowBettingSlip(false), []);

  // Drag is driven straight through the DOM (no per-frame React re-render, which
  // is what made it laggy) - we only touch state on release to close.
  const onHandlePointerDown = (e) => {
    const width = panelRef.current?.offsetWidth || DRAWER_MAX_WIDTH;
    dragStart.current = { x: e.clientX, width };
    dragCurrent.current = 0;
    if (panelRef.current) panelRef.current.style.transition = 'none';
    if (backdropRef.current) backdropRef.current.style.transition = 'none';
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onHandlePointerMove = (e) => {
    if (!dragStart.current) return;
    // Only allow dragging right (toward closed); clamp left at the open position.
    const dx = Math.max(0, e.clientX - dragStart.current.x);
    dragCurrent.current = dx;
    if (panelRef.current) panelRef.current.style.transform = `translateX(${dx}px)`;
    if (backdropRef.current) {
      backdropRef.current.style.opacity = String(Math.max(0, 0.55 * (1 - dx / dragStart.current.width)));
    }
  };
  const onHandlePointerUp = () => {
    if (!dragStart.current) return;
    const { width } = dragStart.current;
    const dx = dragCurrent.current;
    dragStart.current = null;
    const shouldClose = dx > width * 0.33;
    // Re-enable transitions so the panel settles smoothly either way.
    if (panelRef.current) panelRef.current.style.transition = SLIDE;
    if (backdropRef.current) backdropRef.current.style.transition = 'opacity 0.28s ease';
    if (shouldClose) {
      if (panelRef.current) panelRef.current.style.transform = 'translateX(100%)';
      if (backdropRef.current) backdropRef.current.style.opacity = '0';
      closeSlip();
    } else {
      if (panelRef.current) panelRef.current.style.transform = 'translateX(0)';
      if (backdropRef.current) backdropRef.current.style.opacity = '0.55';
    }
  };
  const [betAmount, setBetAmount] = useState(MIN_BET_AMOUNT);
  const [placingBet, setPlacingBet] = useState(false);
  const [balance, setBalance] = useState(user?.balance);
  const [betMode, setBetMode] = useState('beast');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPlayerModal, setShowPlayerModal] = useState(false);
  const [selectedProp, setSelectedProp] = useState(null);

  // API Data State
  const [gamesData, setGamesData] = useState({});
  const [loading, setLoading] = useState(true);
  const [, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [, setDataVersion] = useState(0);

  const beastMultipliers = {
    2: 3, 3: 6, 4: 10, 5: 20, 6: 35
  };

  const safetyMultipliers = {
    2: 2.5, 3: 4, 4: 7, 5: 12, 6: 20
  };

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  // Map API icon names to Lucide React icons
  const getSportIcon = (iconName) => {
    const iconMap = {
      'american-football-outline': Zap,
      'american-football': Zap,
      'football-outline': Target,
      'football': Target,
      'basketball-outline': Dribbble,
      'basketball': Dribbble,
      'tennis-outline': Activity,
      'tennis': Activity,
      'golf-outline': MapPin,
      'golf': MapPin,
      'baseball-outline': Star,
      'baseball': Star,
      'ice-hockey-outline': Grid,
      'ice-hockey': Grid,
      'cricket': Activity,
      'cricket-outline': Activity,
      'soccer': Target,
      'soccer-outline': Target
    };
    
    const IconComponent = iconMap[iconName] || Target;
    return IconComponent;
  };

  // Fetch available sports with data - only include sports with actual player data
  const fetchAvailableSports = async () => {
    try {
      setSportsLoading(true);
      console.log('Fetching available sports with data...');
      
      const response = await fetch(`${API_BASE_URL}/games/sports-with-data`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log(`Sports API response status: ${response.status}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch sports`);
      }

      const sports = await response.json();
      console.log('Available sports with data:', sports);

      if (sports && Array.isArray(sports) && sports.length > 0) {
        // Validate every sport IN PARALLEL (was sequential with a 200ms sleep
        // between each). Keep each valid sport's board so the active sport
        // doesn't get re-fetched right after.
        const results = await Promise.all(
          sports.map(async (sport) => {
            try {
              const gamesResponse = await fetch(`${API_BASE_URL}/games/sports/${sport.key}/games-with-props?maxGames=6`);
              if (!gamesResponse.ok) return { sport, games: null };
              const games = await gamesResponse.json();
              const hasValidPlayers =
                Array.isArray(games) &&
                games.length > 0 &&
                games.some(game =>
                  Array.isArray(game.players) &&
                  game.players.length > 0 &&
                  game.players.some(player => Array.isArray(player.stats) && player.stats.length > 0)
                );
              return { sport, games: hasValidPlayers ? games : null };
            } catch (sportError) {
              console.log(`❌ Error validating sport ${sport.key}:`, sportError.message);
              return { sport, games: null };
            }
          })
        );

        const validated = results.filter(r => r.games);
        const validatedSports = validated.map(r => r.sport);

        // Seed the board cache so switching to the default sport is instant.
        if (validated.length > 0) {
          const boards = {};
          validated.forEach(r => { boards[r.sport.key] = r.games; });
          setGamesData(prev => ({ ...prev, ...boards }));
        }

        if (validatedSports.length > 0) {
          // Sort sports by priority, then by label
          const sortedSports = validatedSports.sort((a, b) => {
            if (a.displayInfo.priority && !b.displayInfo.priority) return -1;
            if (!a.displayInfo.priority && b.displayInfo.priority) return 1;
            return a.displayInfo.label.localeCompare(b.displayInfo.label);
          });

          setAvailableSports(sortedSports);
          
          // Set the first available sport as active if none is selected
          if (!activeSport && sortedSports.length > 0) {
            const defaultSport = sortedSports[0];
            setActiveSport(defaultSport.key);
            console.log(`Set default sport to: ${defaultSport.key}`);
          }
        } else {
          console.warn('No sports with valid player data found');
          setAvailableSports([]);
          setActiveSport(null);
        }
      } else {
        console.warn('No sports data returned from API');
        setAvailableSports([]);
        setActiveSport(null);
      }
    } catch (error) {
      console.error('Error fetching available sports:', error);
      setAvailableSports([]);
      setActiveSport(null);
    } finally {
      setSportsLoading(false);
      // Board is ready (or failed) - dismiss the post-login loading splash.
      if (props.onBoardReady) props.onBoardReady();
    }
  };

  // Fetch games data from API
  const fetchGamesData = async (sportKey, forceRefresh = false) => {
    try {
      if (!forceRefresh) setLoading(true);
      setError(null);

      console.log(`Fetching games for sport: ${sportKey}`);
      
      const response = await fetch(`${API_BASE_URL}/games/sports/${sportKey}/games-with-props?maxGames=6`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log(`Games API response status: ${response.status}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch games`);
      }

      const games = await response.json();
      console.log('Raw API Response:', games);

      // Validate that response has usable data
      if (!games || !Array.isArray(games) || games.length === 0) {
        throw new Error('No games available for this sport');
      }

      // Check if any games have player data
      const hasValidPlayers = games.some(game => 
        game.players && 
        Array.isArray(game.players) && 
        game.players.length > 0 &&
        game.players.some(player => 
          player.stats && 
          Array.isArray(player.stats) && 
          player.stats.length > 0
        )
      );

      if (!hasValidPlayers) {
        throw new Error('No player prop data available for this sport');
      }

      setGamesData(prev => ({
        ...prev,
        [sportKey]: games
      }));
      
      setDataVersion(v => v + 1);
      console.log(`Successfully loaded ${games.length} games for ${sportKey}`);

    } catch (error) {
      console.error(`Error fetching games for ${sportKey}:`, error);
      setError(`Failed to load ${getActiveSportLabel()} games. ${error.message}`);
      
      // Remove the sport from available sports if it consistently fails
      setAvailableSports(prev => prev.filter(sport => sport.key !== sportKey));
      
      // If this was the active sport, switch to another one or clear selection
      if (activeSport === sportKey) {
        const remainingSports = availableSports.filter(sport => sport.key !== sportKey);
        if (remainingSports.length > 0) {
          setActiveSport(remainingSports[0].key);
        } else {
          setActiveSport(null);
        }
      }
      
      // Clear the games data for this sport
      setGamesData(prev => {
        const newData = { ...prev };
        delete newData[sportKey];
        return newData;
      });
      
      setDataVersion(v => v + 1);
    } finally {
      setLoading(false);
      if (forceRefresh) setRefreshing(false);
    }
  };

  // Get active sport label
  const getActiveSportLabel = () => {
    const sport = availableSports.find(s => s.key === activeSport);
    return sport?.displayInfo?.label || 'Unknown Sport';
  };

  // Load sports on component mount
  useEffect(() => {
    fetchAvailableSports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load games when active sport changes
  useEffect(() => {
    if (activeSport) {
      if (gamesData[activeSport]) {
        setLoading(false);
      } else {
        fetchGamesData(activeSport);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSport]);

  // Auto-open betting slip when 6 picks are selected
  useEffect(() => {
    if (bettingSlip.length === 6) {
      alert('🎯 Maximum Picks Reached!\n\nYou\'ve selected 6 picks - the maximum allowed. Your betting slip will open automatically.');
      setTimeout(() => {
        setShowBettingSlip(true);
      }, 100);
    }
  }, [bettingSlip.length]);

  const convertApiDataToProps = (games) => {
    if (!games || !Array.isArray(games) || games.length === 0) {
      return [];
    }
    
    const props = [];
    
    games.forEach(game => {
      if (!game?.players || !Array.isArray(game.players)) return;
      
      game.players.forEach(player => {
        if (!player?.stats || !Array.isArray(player.stats)) return;
        
        player.stats.forEach(stat => {
          // Nothing bettable on either side -> not a card. The backend guarantees a
          // bettable Over, so this only bites on a board cached under an older odds
          // band; without it the card would render with no buttons at all.
          if (!inBand(stat.overOdds) && !inBand(stat.underOdds)) return;

          props.push({
            id: `${game.id}_${player.id || player.name}_${stat.type}`,
            gameId: String(game.id),
            // Raw ISO start time, threaded onto the pick so settlement can anchor
            // the game date / review deadline off the actual game end (parity with
            // mobile). Without it the backend falls back to placement+24h.
            gameTime: game.gameTime,
            player: {
              name: player.name,
              team: player.team,
              avatar: player.avatar || generatePlayerAvatar(player.name)
            },
            match: {
              home: game.homeTeam,
              away: game.awayTeam,
              time: formatGameTime(game.gameTime)
            },
            stat: stat.type,
            line: stat.line,
            overOdds: stat.overOdds,
            underOdds: stat.underOdds,
            // Real last-5 from the API (ESPN-sourced); empty when unavailable
            recentForm: Array.isArray(stat.recentForm) ? stat.recentForm : [],
            gameStatus: game.status || 'Upcoming'
          });
        });
      });
    });
    
    return props;
  };

  const generatePlayerAvatar = (playerName) => {
    const parts = (playerName || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      // First + last initial (single-name players like "Neymar" -> "N").
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return parts[0] ? parts[0][0].toUpperCase() : '?';
  };

  const formatGameTime = (gameTimeISO) => {
    const gameDate = new Date(gameTimeISO);
    const now = new Date();
    const diffHours = Math.ceil((gameDate - now) / (1000 * 60 * 60));
    
    if (diffHours < 2) {
      return 'Starting Soon';
    } else if (diffHours < 24) {
      return `Today ${gameDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    } else if (diffHours < 48) {
      return `Tomorrow ${gameDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    } else {
      return gameDate.toLocaleDateString('en-US', { 
        weekday: 'short',
        hour: 'numeric', 
        minute: '2-digit'
      });
    }
  };

  const getCurrentSportData = () => {
    const data = gamesData[activeSport];
    
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }
    
    const props = convertApiDataToProps(data);

    // Apply search filter if search term exists
    if (searchTerm.trim().length > 0) {
      const searchLower = searchTerm.toLowerCase().trim();
      return props.filter(prop => {
        return (
          prop.player.name.toLowerCase().includes(searchLower) ||
          prop.player.team.toLowerCase().includes(searchLower) ||
          prop.stat.toLowerCase().includes(searchLower) ||
          prop.match.home.toLowerCase().includes(searchLower) ||
          prop.match.away.toLowerCase().includes(searchLower)
        );
      });
    }
    
    return props;
  };

  const getMultiplier = (numSelections) => {
    const multipliers = betMode === 'beast' ? beastMultipliers : safetyMultipliers;
    return multipliers[numSelections] || multipliers[6];
  };

  const addToBettingSlip = (prop, selection) => {
    const existingBetIndex = bettingSlip.findIndex(bet => bet.propId === prop.id);
    const selectedOdds = selection === 'over' ? prop.overOdds : prop.underOdds;
    const isRemoving = existingBetIndex !== -1 && bettingSlip[existingBetIndex].selection === selection;

    // Enforce the odds band (keeps the flat multiplier fair); removal is always ok.
    if (!isRemoving && !inBand(selectedOdds)) {
      alert(`Pick not available\n\nThis pick (odds ${selectedOdds || 'n/a'}) is too far from even money. Only picks between ${MIN_PICK_ODDS} and ${MAX_PICK_ODDS} can be added.`);
      return;
    }

    if (existingBetIndex !== -1) {
      if (isRemoving) {
        setBettingSlip(bettingSlip.filter(bet => bet.propId !== prop.id));
      } else {
        const updatedSlip = [...bettingSlip];
        updatedSlip[existingBetIndex].selection = selection;
        updatedSlip[existingBetIndex].odds = selectedOdds;
        setBettingSlip(updatedSlip);
      }
    } else {
      // Check max picks
      if (bettingSlip.length >= 6) {
        alert('Maximum picks reached\n\nYou can select a maximum of 6 picks for a parlay.');
        return;
      }

      const newBet = {
        propId: prop.id,
        gameId: prop.gameId,
        gameTime: prop.gameTime,
        // Sport being viewed when this pick was added, so settlement verifies
        // against the right feed instead of guessing from the stat name.
        sportKey: activeSport,
        player: prop.player,
        match: prop.match,
        stat: prop.stat,
        line: prop.line,
        selection: selection,
        odds: selectedOdds
      };
      setBettingSlip([...bettingSlip, newBet]);

      if (bettingSlip.length >= 1) {
        setShowBettingSlip(true);
      }
    }
  };

  const handlePlaceBet = async () => {
    if (bettingSlip.length < 2) return;

    if (betAmount < MIN_BET_AMOUNT || betAmount > MAX_BET_AMOUNT) {
      alert(`Bet amount must be between ${formatMoney(MIN_BET_AMOUNT)} and ${formatMoney(MAX_BET_AMOUNT)}`);
      return;
    }

    setPlacingBet(true);
    try {
      const result = await placeBetApi({
        userId: user.id,
        type: betMode === 'beast' ? 2 : 1, // BetType: 1=SafePlay, 2=BeastMode
        amount: betAmount,
        picks: bettingSlip.map(pick => ({
          gameId: pick.gameId,
          gameTime: pick.gameTime,
          sportKey: pick.sportKey,
          teamName: pick.player.team,
          // Fixture's two teams, for the My Bets matchup + reliable settlement resolution.
          homeTeam: pick.match?.home,
          awayTeam: pick.match?.away,
          playerName: pick.player.name,
          statType: pick.stat,
          line: pick.line,
          pickType: pick.selection === 'over' ? 1 : 2, // PickType: 1=Over, 2=Under
          odds: pick.odds, // real odds so the backend's odds-band check applies
        })),
      });

      const newBalance = (balance ?? user?.balance ?? 0) - betAmount;
      setBalance(newBalance);

      // Keep localStorage in sync so a refresh shows the right balance
      try {
        const savedUser = JSON.parse(localStorage.getItem('playerbet_user'));
        if (savedUser) {
          savedUser.balance = newBalance;
          localStorage.setItem('playerbet_user', JSON.stringify(savedUser));
        }
      } catch (storageError) {
        console.error('Failed to update stored balance:', storageError);
      }

      alert(
        `Bet Placed!\n\nYour ${betMode === 'beast' ? 'Beast Mode' : 'Safety Play'} parlay with ` +
        `${bettingSlip.length} picks is in for ${formatMoney(betAmount)}.\n` +
        `Potential payout: ${formatMoney(result?.bet?.potentialPayout ?? betAmount * getMultiplier(bettingSlip.length))}`
      );
      clearBettingSlip();
      setShowBettingSlip(false);
    } catch (error) {
      console.error('Error placing bet:', error);
      alert(`Failed to place bet:\n\n${error.message}`);
    } finally {
      setPlacingBet(false);
    }
  };

  const removeBetFromSlip = (propId) => {
    setBettingSlip(bettingSlip.filter(bet => bet.propId !== propId));
  };

  const clearBettingSlip = () => {
    setBettingSlip([]);
  };

  const calculateAverageForForm = (form) => {
    if (!form || form.length === 0) return '0.0';
    return (form.reduce((a, b) => a + b, 0) / form.length).toFixed(1);
  };

  const handleCardClick = (prop) => {
    setSelectedProp(prop);
    setShowPlayerModal(true);
  };

  // Handle sport selection - only allow if sport exists in available sports
  const handleSportSelection = (sportKey) => {
    const sport = availableSports.find(s => s.key === sportKey);
    if (sport) {
      setActiveSport(sportKey);
    }
  };

  const PlayerPropCard = ({ prop }) => {
    const selectedBet = bettingSlip.find(bet => bet.propId === prop.id);
    const isOverSelected = selectedBet?.selection === 'over';
    const isUnderSelected = selectedBet?.selection === 'under';
    // Only near-coin-flip picks are bettable (flat-multiplier fairness).
    const overEnabled = inBand(prop.overOdds);
    const underEnabled = inBand(prop.underOdds);
    // Render a side only when it is actually bettable - never a dead grey button.
    // A side is unbettable either because the book didn't price it (one-sided
    // goalscorer / soccer shots, odds 0) or because its price sits outside the
    // band; both look the same to the user, so both simply don't appear and the
    // offered side fills the row (matches mobile).
    // An already-selected pick stays rendered so it can be deselected from the
    // card, even if its odds drifted out of band after it was added.
    const overAvailable = overEnabled || isOverSelected;
    const underAvailable = underEnabled || isUnderSelected;
    const avgForm = calculateAverageForForm(prop.recentForm);

    return (
      <div 
        onClick={() => handleCardClick(prop)}
        style={{
          background: selectedBet 
            ? 'linear-gradient(145deg, #1e3a8a 0%, #1f2937 50%, #1f2937 100%)' 
            : 'linear-gradient(145deg, #374151 0%, #1f2937 50%, #111827 100%)',
          borderRadius: '16px',
          overflow: 'hidden',
          border: selectedBet ? '2px solid #3b82f6' : '1px solid #4b5563',
          transition: 'all 0.3s ease-in-out',
          boxShadow: selectedBet 
            ? '0 8px 32px rgba(59, 130, 246, 0.3)' 
            : '0 4px 16px rgba(0, 0, 0, 0.2)',
          position: 'relative',
          cursor: 'pointer',
          minWidth: '280px'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = selectedBet 
            ? '0 12px 40px rgba(59, 130, 246, 0.4)' 
            : '0 8px 24px rgba(0, 0, 0, 0.3)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = selectedBet 
            ? '0 8px 32px rgba(59, 130, 246, 0.3)' 
            : '0 4px 16px rgba(0, 0, 0, 0.2)';
        }}
      >
        {selectedBet && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(145deg, rgba(59, 130, 246, 0.1) 0%, transparent 50%)',
            borderRadius: '16px',
            pointerEvents: 'none'
          }} />
        )}

        {/* Match Header - matches mobile */}
        <div style={{
          background: 'linear-gradient(90deg, #0f172a, #1e293b)',
          padding: '12px 16px',
          borderBottom: '1px solid #475569',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, transparent 0%, rgba(59, 130, 246, 0.05) 50%, transparent 100%)'
          }} />
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '12px',
            color: '#cbd5e1',
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: '600'
            }}>
              <div style={{
                width: '6px',
                height: '6px',
                backgroundColor: '#10b981',
                borderRadius: '50%',
                boxShadow: '0 0 8px rgba(16, 185, 129, 0.6)'
              }} />
              {prop.match.home} vs {prop.match.away}
            </div>
            <div style={{
              backgroundColor: 'rgba(59, 130, 246, 0.2)',
              padding: '4px 8px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '600',
              color: '#60a5fa'
            }}>
              {prop.match.time}
            </div>
          </div>
        </div>

        <div style={{ padding: '20px' }}>
          {/* Player Section - matches mobile */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '20px'
          }}>
            <div style={{
              width: '56px',
              height: '56px',
              background: 'linear-gradient(135deg, #6366f1, #3b82f6)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              fontWeight: 'bold',
              color: 'white',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                top: '-2px',
                left: '-2px',
                right: '-2px',
                bottom: '-2px',
                background: 'linear-gradient(45deg, #6366f1, #3b82f6, #8b5cf6)',
                borderRadius: '50%',
                zIndex: -1,
                opacity: 0.7
              }} />
              {prop.player.avatar}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                marginBottom: '4px'
              }}>
                <div style={{ 
                  color: 'white', 
                  fontSize: '18px', 
                  fontWeight: '700',
                  lineHeight: '20px'
                }}>
                  {shortenPlayerName(prop.player.name).split(' ')[0]}
                </div>
                <div style={{ 
                  color: 'white', 
                  fontSize: '18px', 
                  fontWeight: '700',
                  lineHeight: '20px'
                }}>
                  {shortenPlayerName(prop.player.name).split(' ').slice(1).join(' ')}
                </div>
              </div>
              <div style={{ 
                color: '#94a3b8', 
                fontSize: '14px',
                fontWeight: '500'
              }}>
                {prop.player.team}
              </div>
            </div>
          </div>

          {/* Stat Section - matches mobile */}
          <div style={{
            background: 'linear-gradient(135deg, #0f172a, #1e293b)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '16px',
            border: '1px solid #334155',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 50%)',
              pointerEvents: 'none'
            }} />
            
            <div style={{
              fontSize: '13px',
              color: '#94a3b8',
              marginBottom: '8px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              position: 'relative',
              zIndex: 1
            }}>
              {prop.stat}
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px',
              position: 'relative',
              zIndex: 1
            }}>
              <div style={{
                fontSize: '32px',
                fontWeight: '900',
                color: 'white',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                display: 'flex',
                alignItems: 'baseline',
                gap: '4px'
              }}>
                {isGoalscorerStat(prop.stat) ? '⚽' : prop.line}
                <span style={{
                  fontSize: '16px',
                  color: '#60a5fa',
                  fontWeight: '600'
                }}>
                  {isGoalscorerStat(prop.stat) ? 'ANYTIME' : 'LINE'}
                </span>
              </div>
              {prop.recentForm.length > 0 && (
              <div style={{
                textAlign: 'right'
              }}>
                <div style={{
                  fontSize: '11px',
                  color: '#64748b',
                  marginBottom: '2px'
                }}>
                  L5 Average
                </div>
                <div style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: parseFloat(avgForm) > prop.line ? '#10b981' : '#ef4444'
                }}>
                  {avgForm}
                </div>
              </div>
              )}
            </div>

            {prop.recentForm.length > 0 && (
            <div style={{
              display: 'flex',
              gap: '6px',
              alignItems: 'center',
              position: 'relative',
              zIndex: 1
            }}>
              <span style={{
                fontSize: '11px',
                color: '#64748b',
                marginRight: '8px',
                fontWeight: '600'
              }}>
                Recent Form:
              </span>
              {prop.recentForm.map((value, index) => (
                <div
                  key={index}
                  style={{
                    width: '28px',
                    height: '28px',
                    backgroundColor: value > prop.line ? '#059669' : '#dc2626',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    color: 'white',
                    fontWeight: 'bold',
                    boxShadow: value > prop.line 
                      ? '0 2px 8px rgba(5, 150, 105, 0.4)' 
                      : '0 2px 8px rgba(220, 38, 38, 0.4)',
                    position: 'relative'
                  }}
                >
                  {value}
                </div>
              ))}
            </div>
            )}
          </div>

          {/* Betting Options - matches mobile */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: (overAvailable && underAvailable) ? '1fr 1fr' : '1fr',
            gap: '12px'
          }}>
            {overAvailable && (
            <button
              disabled={!overEnabled && !isOverSelected}
              onClick={(e) => {
                e.stopPropagation();
                addToBettingSlip(prop, 'over');
              }}
              style={{
                padding: '16px',
                background: isOverSelected
                  ? 'linear-gradient(135deg, #059669, #10b981)'
                  : (!overEnabled ? '#1f2937' : 'linear-gradient(135deg, #4b5563, #374151)'),
                border: isOverSelected ? '2px solid #10b981' : '1px solid #6b7280',
                borderRadius: '12px',
                color: (!overEnabled && !isOverSelected) ? '#6b7280' : 'white',
                opacity: (!overEnabled && !isOverSelected) ? 0.5 : 1,
                cursor: (!overEnabled && !isOverSelected) ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                fontWeight: isOverSelected ? '700' : '600',
                boxShadow: isOverSelected 
                  ? '0 4px 16px rgba(16, 185, 129, 0.4)' 
                  : '0 2px 8px rgba(0, 0, 0, 0.2)',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ fontSize: '14px', marginBottom: '6px', opacity: 0.9 }}>
                  {isGoalscorerStat(prop.stat) ? 'TO SCORE' : 'OVER'}
                </div>
                {isOverSelected && (
                  <div style={{ 
                    fontSize: '12px', 
                    marginTop: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px'
                  }}>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div style={{ fontSize: '8px', color: '#059669' }}>✓</div>
                    </div>
                    Selected
                  </div>
                )}
              </div>
            </button>
            )}
            {underAvailable && (
            <button
              disabled={!underEnabled && !isUnderSelected}
              onClick={(e) => {
                e.stopPropagation();
                addToBettingSlip(prop, 'under');
              }}
              style={{
                padding: '16px',
                background: isUnderSelected
                  ? 'linear-gradient(135deg, #059669, #10b981)'
                  : (!underEnabled ? '#1f2937' : 'linear-gradient(135deg, #4b5563, #374151)'),
                border: isUnderSelected ? '2px solid #10b981' : '1px solid #6b7280',
                borderRadius: '12px',
                color: (!underEnabled && !isUnderSelected) ? '#6b7280' : 'white',
                opacity: (!underEnabled && !isUnderSelected) ? 0.5 : 1,
                cursor: (!underEnabled && !isUnderSelected) ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                fontWeight: isUnderSelected ? '700' : '600',
                boxShadow: isUnderSelected 
                  ? '0 4px 16px rgba(16, 185, 129, 0.4)' 
                  : '0 2px 8px rgba(0, 0, 0, 0.2)',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ fontSize: '14px', marginBottom: '6px', opacity: 0.9 }}>
                  {isGoalscorerStat(prop.stat) ? 'NO GOAL' : 'UNDER'}
                </div>
                {isUnderSelected && (
                  <div style={{ 
                    fontSize: '12px', 
                    marginTop: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px'
                  }}>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div style={{ fontSize: '8px', color: '#059669' }}>✓</div>
                    </div>
                    Selected
                  </div>
                )}
              </div>
            </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Player Details Modal - matches mobile
  const PlayerDetailsModal = () => {
    if (!selectedProp) return null;

    const avgForm = calculateAverageForForm(selectedProp.recentForm);
    const isAboveLine = parseFloat(avgForm) > selectedProp.line;

    return showPlayerModal && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '20px'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          backgroundColor: '#000',
          borderRadius: '20px',
          overflow: 'hidden',
          border: '1px solid #374151'
        }}>
          {/* Modal Header */}
          <div style={{
            background: 'linear-gradient(135deg, #7c3aed, #2563eb)',
            padding: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '50px',
                height: '50px',
                background: 'linear-gradient(135deg, #6366f1, #3b82f6)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                fontWeight: 'bold',
                color: 'white'
              }}>
                {selectedProp.player.avatar}
              </div>
              <div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'white' }}>
                  {selectedProp.player.name}
                </div>
                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.8)' }}>
                  {selectedProp.player.team}
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowPlayerModal(false)}
              style={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                padding: '8px',
                borderRadius: '20px',
                border: 'none',
                cursor: 'pointer',
                color: 'white'
              }}
            >
              <X size={24} />
            </button>
          </div>

          <div style={{
            padding: '20px',
            maxHeight: '70vh',
            overflowY: 'auto'
          }}>
            {/* Match Details */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: 'white', marginBottom: '12px' }}>
                Match Details
              </h3>
              <div style={{
                background: 'linear-gradient(135deg, #1f2937, #111827)',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid #374151'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#9ca3af' }}>Match:</span>
                  <span style={{ color: 'white', fontWeight: '600' }}>
                    {selectedProp.match.home} vs {selectedProp.match.away}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#9ca3af' }}>Time:</span>
                  <span style={{ color: 'white', fontWeight: '600' }}>{selectedProp.match.time}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#9ca3af' }}>Status:</span>
                  <span style={{ color: 'white', fontWeight: '600' }}>{selectedProp.gameStatus}</span>
                </div>
              </div>
            </div>

            {/* Stat Analysis */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: 'white', marginBottom: '12px' }}>
                {selectedProp.stat} Analysis
              </h3>
              <div style={{
                background: 'linear-gradient(135deg, #1f2937, #111827)',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid #374151'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '36px', fontWeight: 'bold', color: 'white' }}>
                      {isGoalscorerStat(selectedProp.stat) ? '⚽' : selectedProp.line}
                    </div>
                    <div style={{ fontSize: '12px', color: '#60a5fa', fontWeight: '600' }}>
                      {isGoalscorerStat(selectedProp.stat) ? 'ANYTIME SCORER' : 'BETTING LINE'}
                    </div>
                  </div>
                  {selectedProp.recentForm.length > 0 && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: 'bold',
                      color: isAboveLine ? '#10b981' : '#ef4444'
                    }}>
                      {avgForm}
                    </div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', fontWeight: '600' }}>
                      L5 AVERAGE
                    </div>
                  </div>
                  )}
                </div>

                {/* Performance Indicator - only meaningful with real form */}
                {selectedProp.recentForm.length > 0 && (
                <div style={{
                  padding: '8px 16px',
                  backgroundColor: isAboveLine ? '#10b981' : '#ef4444',
                  borderRadius: '8px',
                  textAlign: 'center',
                  marginBottom: '16px'
                }}>
                  <span style={{ fontSize: '12px', fontWeight: 'bold', color: 'white' }}>
                    {isAboveLine ? 'TRENDING OVER' : 'TRENDING UNDER'}
                  </span>
                </div>
                )}

                {/* Recent Form */}
                {selectedProp.recentForm.length > 0 && (
                <div>
                  <h4 style={{ fontSize: '16px', fontWeight: 'bold', color: 'white', marginBottom: '12px' }}>
                    Last 5 Games Performance
                  </h4>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {selectedProp.recentForm.map((value, index) => (
                      <div key={index} style={{
                        flex: 1,
                        backgroundColor: '#374151',
                        borderRadius: '8px',
                        padding: '12px',
                        textAlign: 'center',
                        border: '1px solid #4b5563'
                      }}>
                        <div style={{ fontSize: '10px', color: '#9ca3af', marginBottom: '4px' }}>
                          Game {5-index}
                        </div>
                        <div style={{
                          fontSize: '18px',
                          fontWeight: 'bold',
                          color: value > selectedProp.line ? '#10b981' : '#ef4444',
                          marginBottom: '4px'
                        }}>
                          {value}
                        </div>
                        <div style={{
                          width: '20px',
                          height: '20px',
                          backgroundColor: value > selectedProp.line ? '#10b981' : '#ef4444',
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto'
                        }}>
                          <TrendingUp size={10} color="white" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: 'white', marginBottom: '12px' }}>
                Quick Actions
              </h3>
              {/* Only offer a side that's actually bettable. These used to render
                  unconditionally, so a one-sided market showed a live-looking
                  button that always failed the band check in addToBettingSlip. */}
              <div style={{ display: 'flex', gap: '12px' }}>
                {inBand(selectedProp.overOdds) && (
                <button
                  onClick={() => {
                    addToBettingSlip(selectedProp, 'over');
                    setShowPlayerModal(false);
                  }}
                  style={{
                    flex: 1,
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '16px 12px',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  <TrendingUp size={20} />
                  {isGoalscorerStat(selectedProp.stat) ? 'Bet To Score' : `Bet Over ${selectedProp.line}`}
                </button>
                )}

                {inBand(selectedProp.underOdds) && (
                <button
                  onClick={() => {
                    addToBettingSlip(selectedProp, 'under');
                    setShowPlayerModal(false);
                  }}
                  style={{
                    flex: 1,
                    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '16px 12px',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  <TrendingUp size={20} style={{ transform: 'rotate(180deg)' }} />
                  {isGoalscorerStat(selectedProp.stat) ? 'Bet No Goal' : `Bet Under ${selectedProp.line}`}
                </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#000',
      color: 'white',
      paddingBottom: '60px'
    }}>
      {/* Header - matches mobile */}
      <div style={{
        background: 'linear-gradient(135deg, #7c3aed, #2563eb)',
        padding: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img src="/pb-logo.png" alt="PlayerBet" style={{ width: '40px', height: '40px' }} />
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>PLAYERBET</h1>
            <p style={{ fontSize: '12px', margin: 0, opacity: 0.9 }}>
              Balance: {formatMoney(balance ?? user?.balance ?? 0)}
            </p>
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={onNavigateToWallet}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              fontWeight: '600'
            }}
          >
            <WalletIcon size={14} />
            Wallet
          </button>

          <button
            onClick={onNavigateToUserBets}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              fontWeight: '600'
            }}
          >
            <History size={14} />
            My Bets
          </button>

          <div
            onClick={onNavigateToProfile}
            style={{
              width: '40px',
              height: '40px',
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            {user?.name ? user.name.charAt(0).toUpperCase() : 'A'}
          </div>
          <button
            onClick={onLogout}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px'
            }}
          >
            <LogOut size={16} />
            Logout
          </button>
        </div>
      </div>

      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '20px'
      }}>
        {/* Dynamic Sports Navigation */}
        {sportsLoading ? (
          <div style={{
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            padding: '16px', 
            gap: '8px'
          }}>
            <div style={{
              width: '16px',
              height: '16px',
              border: '2px solid #2563eb',
              borderTop: '2px solid transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <span style={{ fontSize: '14px', color: '#9ca3af' }}>Loading sports...</span>
          </div>
        ) : availableSports.length > 0 ? (
          <div style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '24px',
            overflowX: 'auto',
            paddingBottom: '8px'
          }}>
            {availableSports.map((sport) => {
              const IconComponent = getSportIcon(sport.displayInfo?.icon);
              return (
                <button
                  key={sport.key}
                  onClick={() => handleSportSelection(sport.key)}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: activeSport === sport.key ? '#2563eb' : '#1f2937',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {sport.displayInfo?.logoUrl ? (
                    <img
                      src={sport.displayInfo.logoUrl}
                      alt=""
                      style={{ width: 22, height: 22, objectFit: 'contain' }}
                    />
                  ) : (
                    <IconComponent size={16} />
                  )}
                  {sport.displayInfo?.label || sport.key}
                </button>
              );
            })}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '40px'
          }}>
            <AlertCircle size={48} color="#6b7280" />
            <p style={{ fontSize: '16px', color: 'white', marginTop: '16px', marginBottom: '8px' }}>
              No sports with player data available
            </p>
            <p style={{ fontSize: '14px', color: '#9ca3af', marginBottom: '24px' }}>
              Check back later for updates
            </p>
            <button 
              onClick={fetchAvailableSports}
              style={{
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              Refresh
            </button>
          </div>
        )}

        {/* Below the chips, and scoped to the selected one. Gated on activeSport
            rather than letting the strip decide: it's null from mount until sports
            load, and rendering unfiltered first would flip twelve leagues of cards
            down to one. Rendering nothing adds no new layout shift - the strip
            already renders nothing that early, its own fetch hasn't landed. */}
        {activeSport && <LiveScoresStrip league={getActiveSportLabel()} />}

        {/* Search Bar */}
        {activeSport && (
          <div style={{
            position: 'relative',
            marginBottom: '24px'
          }}>
            <Search size={20} style={{
              position: 'absolute',
              left: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af'
            }} />
            <input
              type="text"
              placeholder="Search for players, teams, or props..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px 12px 48px',
                backgroundColor: '#1f2937',
                border: '1px solid #374151',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#2563eb'}
              onBlur={(e) => e.target.style.borderColor = '#374151'}
            />
            {searchTerm.length > 0 && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#9ca3af',
                  cursor: 'pointer'
                }}
              >
                <X size={20} />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        {!activeSport ? (
          <div style={{
            textAlign: 'center',
            padding: '60px'
          }}>
            <p style={{ fontSize: '16px', color: '#9ca3af' }}>
              Select a sport to view games
            </p>
          </div>
        ) : loading ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px'
          }}>
            <div style={{
              width: '32px',
              height: '32px',
              border: '3px solid #2563eb',
              borderTop: '3px solid transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginBottom: '16px'
            }} />
            <p style={{ fontSize: '16px', color: '#9ca3af' }}>Loading games...</p>
          </div>
        ) : error ? (
          <div style={{
            textAlign: 'center',
            padding: '40px'
          }}>
            <AlertCircle size={48} color="#ef4444" />
            <p style={{ fontSize: '18px', fontWeight: 'bold', color: 'white', marginTop: '16px', marginBottom: '8px' }}>
              Unable to load games
            </p>
            <p style={{ fontSize: '14px', color: '#9ca3af', textAlign: 'center', marginBottom: '24px' }}>
              {error}
            </p>
            <button 
              onClick={() => activeSport && fetchGamesData(activeSport)}
              style={{
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              Retry
            </button>
          </div>
        ) : (
          <div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '20px'
            }}>
              {getCurrentSportData().map(prop => (
                <PlayerPropCard key={prop.id} prop={prop} />
              ))}
              {getCurrentSportData().length === 0 && (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  gridColumn: '1 / -1'
                }}>
                  <AlertCircle size={48} color="#6b7280" />
                  <p style={{ fontSize: '16px', color: 'white', marginTop: '16px', marginBottom: '8px' }}>
                    No games available for {getActiveSportLabel()}
                  </p>
                  <p style={{ fontSize: '14px', color: '#9ca3af', textAlign: 'center' }}>
                    Try refreshing or check back later
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Player Details Modal */}
      <PlayerDetailsModal />

      {/* Enhanced Betting Slip FAB - matches mobile */}
      {bettingSlip.length > 0 && (
        <button
          onClick={() => setShowBettingSlip(!showBettingSlip)}
          style={{
            position: 'fixed',
            bottom: '130px',
            right: '20px',
            width: '60px',
            height: '60px',
            backgroundColor: bettingSlip.length >= 2 ? '#2563eb' : '#6b7280',
            color: 'white',
            borderRadius: '50%',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: bettingSlip.length >= 2
              ? '0 8px 32px rgba(37, 99, 235, 0.4)'
              : '0 4px 12px rgba(0, 0, 0, 0.3)',
            zIndex: 100,
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          {/* Pull-out hint: arrow drawn inside the circle tells the user the
              slip drawer slides out when the floater is pressed. */}
          <div style={{
            position: 'absolute',
            left: '5px',
            top: '22px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <ChevronLeft size={14} color="rgba(255,255,255,0.9)" />
          </div>
          <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{bettingSlip.length}</div>
          <div style={{ fontSize: '10px' }}>PICKS</div>
          {bettingSlip.length >= 2 && (
            <div style={{
              position: 'absolute',
              top: '-5px',
              right: '-5px',
              backgroundColor: '#10b981',
              borderRadius: '10px',
              padding: '2px 6px',
              fontSize: '8px',
              fontWeight: 'bold',
              border: '2px solid #000'
            }}>
              {bettingSlip.length === 6 ? 'MAX!' : 'Ready!'}
            </div>
          )}
        </button>
      )}

      {/* Enhanced Betting Slip Modal - matches mobile */}
      {slipMounted && (
          <>
            {/* Dimmed backdrop - click the exposed area to slide the slip away */}
            <div
              ref={backdropRef}
              onClick={closeSlip}
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: '#000',
                opacity: slipOpen ? 0.55 : 0,
                transition: 'opacity 0.28s ease',
                zIndex: 999,
              }}
            />
            {/* Panel - pulls in from the right, covers most of the screen */}
            <div
              ref={panelRef}
              onTransitionEnd={(e) => {
                if (e.propertyName === 'transform' && !showBettingSlip) setSlipMounted(false);
              }}
              style={{
                position: 'fixed',
                top: 0,
                right: 0,
                width: 'min(92vw, 460px)',
                height: '100vh',
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'row',
                transform: slipOpen ? 'translateX(0)' : 'translateX(100%)',
                transition: SLIDE,
                boxShadow: '-8px 0 28px rgba(0,0,0,0.45)',
                willChange: 'transform',
              }}
            >
              {/* Left-edge grab handle - drag right to push the slip back off-screen */}
              <div
                onPointerDown={onHandlePointerDown}
                onPointerMove={onHandlePointerMove}
                onPointerUp={onHandlePointerUp}
                onPointerCancel={onHandlePointerUp}
                style={{
                  width: '26px',
                  flexShrink: 0,
                  backgroundColor: '#0b1220',
                  borderRight: '1px solid #1f2937',
                  cursor: 'grab',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  touchAction: 'none',
                }}
              >
                <div style={{ width: '4px', height: '44px', borderRadius: '2px', backgroundColor: '#4b5563' }} />
              </div>

              {/* Content column (the existing slip UI) */}
              <div style={{
                flex: 1,
                minWidth: 0,
                backgroundColor: '#111827',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}>
          {/* Header */}
          <div style={{
            padding: '16px',
            backgroundColor: '#1f2937',
            borderBottom: '1px solid #374151',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h3 style={{
              margin: 0,
              fontSize: '17px',
              fontWeight: 'bold',
              flexShrink: 1,
              marginRight: '10px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              Parlay Builder ({bettingSlip.length}/6)
            </h3>
            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
              <button
                onClick={clearBettingSlip}
                style={{
                  backgroundColor: '#374151',
                  color: '#ef4444',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px'
                }}
              >
                <Trash2 size={14} />
                Clear
              </button>
              <button
                onClick={() => setShowBettingSlip(false)}
                style={{
                  backgroundColor: '#374151',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px',
                  cursor: 'pointer'
                }}
              >
                <X size={18} />
              </button>
            </div>
          </div>

          <div style={{ flex: 1, padding: '16px', paddingBottom: '100px' }}>
            {/* Warning for insufficient picks */}
            {bettingSlip.length < 2 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px',
                backgroundColor: '#7c2d12',
                borderRadius: '8px',
                gap: '8px',
                marginBottom: '16px'
              }}>
                <AlertCircle size={18} color="#fca5a5" />
                <div>
                  <div style={{ color: '#fca5a5', fontSize: '14px', fontWeight: 'bold' }}>
                    Minimum 2 picks required
                  </div>
                  <div style={{ color: '#fca5a5', fontSize: '12px', opacity: 0.9 }}>
                    Add {2 - bettingSlip.length} more selection{2 - bettingSlip.length > 1 ? 's' : ''} to place bet
                  </div>
                </div>
              </div>
            )}

            {/* Bet Mode Selection - Enhanced like mobile */}
            {bettingSlip.length >= 2 && (
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '16px', textAlign: 'center' }}>
                  Choose Your Bet Mode
                </h4>

                {/* Segmented toggle - full width so the labels never cramp */}
                <div style={{ display: 'flex', gap: '4px', backgroundColor: '#0b1220', border: '1px solid #374151', borderRadius: '12px', padding: '4px', marginBottom: '12px' }}>
                  <button
                    onClick={() => setBetMode('safety')}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      padding: '11px', borderRadius: '9px', border: 'none', cursor: 'pointer',
                      fontSize: '13px', fontWeight: 700,
                      background: betMode === 'safety' ? '#059669' : 'transparent',
                      color: betMode === 'safety' ? '#fff' : '#9ca3af',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <Shield size={16} /> Safety Play
                  </button>
                  <button
                    onClick={() => setBetMode('beast')}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      padding: '11px', borderRadius: '9px', border: 'none', cursor: 'pointer',
                      fontSize: '13px', fontWeight: 700,
                      background: betMode === 'beast' ? '#dc2626' : 'transparent',
                      color: betMode === 'beast' ? '#fff' : '#9ca3af',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <Flame size={16} /> Beast Mode
                  </button>
                </div>

                {/* Details for the active mode - full width, roomy text */}
                <div style={{
                  borderRadius: '12px', padding: '14px',
                  border: betMode === 'safety' ? '1px solid #065f46' : '1px solid #991b1b',
                  background: betMode === 'safety' ? 'rgba(6,78,59,0.35)' : 'rgba(127,29,29,0.35)',
                }}>
                  <div style={{ fontSize: '12.5px', color: '#d1d5db', lineHeight: '18px', marginBottom: '10px' }}>
                    {betMode === 'safety'
                      ? `Hit all ${bettingSlip.length}, or at least ${Math.ceil(bettingSlip.length * 0.7)} of ${bettingSlip.length}, and you still get paid.`
                      : `All ${bettingSlip.length} picks must hit - higher risk, bigger payout.`}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                    <span style={{ fontSize: '13px', color: '#cbd5e1' }}>All {bettingSlip.length} correct</span>
                    <span style={{ fontSize: '16px', fontWeight: 'bold', color: betMode === 'safety' ? '#34d399' : '#f87171' }}>
                      {getMultiplier(bettingSlip.length)}x
                    </span>
                  </div>
                  {betMode === 'safety' ? (
                    bettingSlip.length >= 3 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                        <span style={{ fontSize: '13px', color: '#cbd5e1' }}>{bettingSlip.length - 1} of {bettingSlip.length} correct</span>
                        <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#34d399' }}>
                          {(getMultiplier(bettingSlip.length) * 0.2).toFixed(1)}x
                        </span>
                      </div>
                    )
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                      <span style={{ fontSize: '13px', color: '#cbd5e1' }}>Miss any pick</span>
                      <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#f87171' }}>0x</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Selected Picks */}
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}>
                Your Picks ({bettingSlip.length}/6)
              </h4>
              {bettingSlip.map((bet, index) => (
                <div key={bet.propId} style={{
                  backgroundColor: '#374151',
                  borderRadius: '8px',
                  border: '1px solid #4b5563',
                  marginBottom: '12px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px',
                    borderBottom: '1px solid #4b5563'
                  }}>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      backgroundColor: '#3b82f6',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      marginRight: '12px',
                      color: 'white'
                    }}>
                      {index + 1}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12px', color: '#e5e7eb', fontWeight: '600' }}>
                        {bet.match.home} vs {bet.match.away}
                      </div>
                      <div style={{ fontSize: '10px', color: '#9ca3af' }}>
                        {bet.match.time}
                      </div>
                    </div>
                    <button
                      onClick={() => removeBetFromSlip(bet.propId)}
                      style={{
                        backgroundColor: 'transparent',
                        color: '#ef4444',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px'
                      }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <div style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        background: 'linear-gradient(135deg, #6366f1, #3b82f6)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        marginRight: '8px',
                        color: 'white'
                      }}>
                        {bet.player.avatar}
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: 'white' }}>
                        {bet.player.name}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                        {bet.stat}
                      </div>
                      <div style={{
                        backgroundColor: '#10b981',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: 'white'
                      }}>
                        {isGoalscorerStat(bet.stat)
                          ? (bet.selection === 'over' ? 'TO SCORE' : 'NO GOAL')
                          : `${bet.selection.toUpperCase()} ${bet.line}`}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Bet Amount and Calculation */}
            {bettingSlip.length >= 2 && (
              <div>
                <h4 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}>
                  Bet Amount
                </h4>
                
                {/* Preset amounts */}
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  marginBottom: '12px'
                }}>
                  {[50, 100, 250, 500].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setBetAmount(amount)}
                      style={{
                        flex: 1,
                        padding: '12px',
                        backgroundColor: betAmount === amount ? '#3b82f6' : '#374151',
                        color: 'white',
                        border: '1px solid ' + (betAmount === amount ? '#3b82f6' : '#4b5563'),
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      {formatMoney(amount)}
                    </button>
                  ))}
                </div>

                {/* Custom amount */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '20px'
                }}>
                  <span style={{ fontSize: '14px', color: '#e5e7eb' }}>Custom Amount:</span>
                  <input
                    type="number"
                    value={betAmount}
                    onChange={(e) => {
                      const amount = parseInt(e.target.value) || 0;
                      setBetAmount(Math.max(1, Math.min(MAX_BET_AMOUNT, amount)));
                    }}
                    style={{
                      flex: 1,
                      backgroundColor: '#374151',
                      border: '1px solid #4b5563',
                      borderRadius: '6px',
                      padding: '12px',
                      color: 'white',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                    placeholder="Enter amount"
                  />
                </div>

                {/* Payout Calculation */}
                <div style={{
                  backgroundColor: '#1f2937',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '20px',
                  border: '1px solid #374151'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px'
                  }}>
                    <span style={{ fontSize: '14px', color: '#9ca3af' }}>
                      {betMode === 'beast' ? 'Beast Mode' : 'Safety Play'} Multiplier
                    </span>
                    <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#10b981' }}>
                      {getMultiplier(bettingSlip.length)}x
                    </span>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '14px', color: '#9ca3af' }}>Stake:</span>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: 'white' }}>
                        {formatMoney(betAmount)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '14px', color: '#9ca3af' }}>Potential Payout:</span>
                      <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#10b981' }}>
                        {formatMoney(betAmount * getMultiplier(bettingSlip.length))}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Place Bet Button */}
                <button
                  onClick={handlePlaceBet}
                  disabled={placingBet}
                  style={{
                    width: '100%',
                    padding: '16px',
                    background: betMode === 'beast' 
                      ? 'linear-gradient(135deg, #ef4444, #dc2626)' 
                      : 'linear-gradient(135deg, #10b981, #059669)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: placingBet ? 'not-allowed' : 'pointer',
                    opacity: placingBet ? 0.6 : 1,
                    transition: 'transform 0.2s ease'
                  }}
                  onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                  onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                >
                  {placingBet ? 'Placing Bet...' : `Place ${betMode === 'beast' ? 'Beast Mode' : 'Safety Play'} Bet`}
                </button>
              </div>
            )}
          </div>
              </div>{/* content column */}
            </div>{/* panel */}
          </>
      )}

      {/* Bottom Navigation - matches mobile */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#111827',
        borderTop: '1px solid #374151',
        display: 'flex',
        justifyContent: 'space-around',
        padding: '12px 0'
      }}>
        <button style={{
          background: 'none',
          border: 'none',
          color: '#2563eb',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '4px'
        }}>
          <Home size={20} />
          <span style={{ fontSize: '10px' }}>Home</span>
        </button>
        <button
          onClick={onNavigateToCopilot}
          style={{
            background: 'none',
            border: 'none',
            color: '#a78bfa',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <Sparkles size={20} />
          <span style={{ fontSize: '10px' }}>Playmaker</span>
        </button>
        <button 
          onClick={onNavigateToUserBets}
          style={{
            background: 'none',
            border: 'none',
            color: '#9ca3af',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <Trophy size={20} />
          <span style={{ fontSize: '10px' }}>My Bets</span>
        </button>
        <button
          onClick={onNavigateToWallet}
          style={{
            background: 'none',
            border: 'none',
            color: '#9ca3af',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <WalletIcon size={20} />
          <span style={{ fontSize: '10px' }}>Wallet</span>
        </button>
        <button
          onClick={onNavigateToProfile}
          style={{
            background: 'none',
            border: 'none',
            color: '#9ca3af',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          <User size={20} />
          <span style={{ fontSize: '10px' }}>Profile</span>
        </button>
      </div>

      {/* Add CSS animations */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;